import type { ActivityEntry, NoteType } from "@/data/types";
import { displayIdentity } from "@/lib/uuid";
import {
  Building2, MessageSquare, HardHat, Users, FileText, Package,
  AlertTriangle, Clock, Wrench,
} from "lucide-react";

const NOTE_TYPE_CONFIG: Record<NoteType, { label: string; icon: React.ElementType; color: string; dotColor: string }> = {
  ops: { label: "Ops", icon: Building2, color: "text-primary", dotColor: "bg-primary" },
  customer: { label: "Customer", icon: MessageSquare, color: "text-info", dotColor: "bg-info" },
  crew: { label: "Crew", icon: HardHat, color: "text-success", dotColor: "bg-success" },
  subcontractor: { label: "Sub", icon: Users, color: "text-accent", dotColor: "bg-accent" },
  permit: { label: "Permit", icon: FileText, color: "text-warning", dotColor: "bg-warning" },
  ordering: { label: "Order", icon: Package, color: "text-primary", dotColor: "bg-primary" },
  issue: { label: "Issue", icon: AlertTriangle, color: "text-destructive", dotColor: "bg-destructive" },
  system: { label: "System", icon: Clock, color: "text-muted-foreground", dotColor: "bg-muted-foreground" },
};

function normalizeDateKey(dateStr: string): string {
  if (!dateStr) return "unknown";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  return parsed.toISOString().slice(0, 10);
}

function formatTimelineDate(dateStr: string): string {
  if (dateStr === "unknown") return "Unknown Date";
  const parsed = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return "Unknown Date";
  return parsed.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

interface ActivityTimelineProps {
  entries: ActivityEntry[];
}

export function ActivityTimeline({ entries }: ActivityTimelineProps) {
  // Group entries by date
  const grouped: Record<string, ActivityEntry[]> = {};
  for (const e of entries) {
    const dateKey = normalizeDateKey(e.date);
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(e);
  }

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-4">
      {sortedDates.map((date) => (
        <div key={date}>
          {/* Date header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              {formatTimelineDate(date)}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Entries for this date */}
          <div className="relative pl-5 border-l-2 border-muted/60 space-y-2.5 ml-1">
            {grouped[date].map((a) => {
              const nt = a.noteType || "ops";
              const config = NOTE_TYPE_CONFIG[nt];
              const Icon = config.icon;

              return (
                <div key={a.id} className="relative group">
                  {/* Timeline dot */}
                  <div className={`absolute -left-[23px] top-1 h-3 w-3 rounded-full border-2 border-background ${config.dotColor}`} />

                  <div className="flex items-start gap-2">
                    {/* Type badge */}
                    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${config.color} bg-muted/40`}>
                      <Icon className="h-2.5 w-2.5" />
                      {config.label}
                    </span>

                   <div className="flex-1 min-w-0">
                       <p className="text-xs leading-snug">{a.action}</p>
                       <p className="text-[10px] text-muted-foreground mt-0.5">{displayIdentity(a.user, "User")}</p>
                     </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
